import { geojsonRbush, lineSegment, segmentEach, featureEach, getCoords, booleanEqual, nearestPointOnLine, booleanPointOnLine, lineString, point, featureCollection } from "@turf/turf";
import { LineString, MultiLineString, MultiPolygon, Polygon, Feature, FeatureCollection, GeoJsonProperties } from "geojson";
import equal from 'deep-equal';

export class Geo {

    // Takes any LineString or Polygon and returns the overlapping lines between both features.
    public static lineOverlap<G1 extends LineString | MultiLineString | Polygon | MultiPolygon, G2 extends LineString | MultiLineString | Polygon | MultiPolygon>(line1: Feature<G1> | G1, line2: Feature<G2> | G2): FeatureCollection<LineString> {
        const tolerance = 0;

        // Containers
        const features: Feature<LineString, GeoJsonProperties>[] = [];

        // Create Spatial Index
        const tree = geojsonRbush<LineString>();

        // To-Do -- HACK way to support typescript
        const line: any = lineSegment(line1);
        tree.load(line);

        // Iterate over line segments
        segmentEach(line2, function (segment) {
            if (!segment) {
                return;
            }

            // Iterate over each segments which falls within the same bounds
            featureEach(tree.search(segment), function (match) {
                const coordsSegment = getCoords(segment).sort();
                const coordsMatch = getCoords(match).sort();

                // Segment overlaps feature
                if (booleanEqual(segment, match)) {
                    Geo.concatSegmentToFeatures(features, segment);
                } else {
                    const segmentStartOnMatch = tolerance
                        ? nearestPointOnLine(match, coordsSegment[0]).properties.dist <=
                        tolerance
                        : booleanPointOnLine(coordsSegment[0], match);
                    const segmentEndOnMatch = tolerance
                        ? nearestPointOnLine(match, coordsSegment[1]).properties.dist <=
                        tolerance
                        : booleanPointOnLine(coordsSegment[1], match);
                    const matchStartOnSegment = tolerance
                        ? nearestPointOnLine(segment, coordsMatch[0]).properties.dist <=
                        tolerance
                        : booleanPointOnLine(coordsMatch[0], segment);
                    const matchEndOnSegment = tolerance
                        ? nearestPointOnLine(segment, coordsMatch[1]).properties.dist <=
                        tolerance
                        : booleanPointOnLine(coordsMatch[1], segment);
                    if (segmentStartOnMatch && segmentEndOnMatch) {
                        Geo.concatSegmentToFeatures(features, segment);
                    } else if (matchStartOnSegment && matchEndOnSegment) {
                        Geo.concatSegmentToFeatures(features, match);
                    } else {
                        const from = segmentStartOnMatch
                            ? coordsSegment[0]
                            : segmentEndOnMatch
                                ? coordsSegment[1]
                                : undefined;
                        const to = matchStartOnSegment
                            ? coordsMatch[0]
                            : matchEndOnSegment
                                ? coordsMatch[1]
                                : undefined;
                        if (from && to) {
                            const isSame = tolerance
                                ? nearestPointOnLine(lineString([from, from]), to).properties
                                    .dist <= tolerance
                                : booleanEqual(point(from), point(to));
                            if (!isSame) {
                                Geo.concatSegmentToFeatures(features, lineString([from, to]));
                            }
                        }
                    }
                }
            });
        });
        return featureCollection(features);
    }

    /**
     * Add segment to list of linestrings
     *
     * @private
     * @param {Feature<LineString, GeoJsonProperties>[]} features Existing list
     * @param {Feature<LineString>} segment 2-vertex LineString to add to list
     */
    private static concatSegmentToFeatures(
        features: Feature<LineString, GeoJsonProperties>[],
        segment: Feature<LineString>
    ): void {
        for (let i = features.length - 1; i >= 0; i--) {
            if (Geo.concatSegment(features[i], segment)) {
                return;
            }
        }
        features.push(segment);
    }

    /**
     * Concat Segment
     *
     * @private
     * @param {Feature<LineString>} line LineString
     * @param {Feature<LineString>} segment 2-vertex LineString
     * @returns {Feature<LineString> | void} concat linestring or nothing if no suitable place could be found in linestring
     */
    private static concatSegment(
        line: Feature<LineString>,
        segment: Feature<LineString>
    ) {
        const coords = getCoords(segment);
        const lineCoords = getCoords(line);
        const start = lineCoords[0];
        const end = lineCoords[lineCoords.length - 1];
        const geom = line.geometry.coordinates;

        if (equal(coords[0], start)) geom.unshift(coords[1]);
        else if (equal(coords[0], end)) geom.push(coords[1]);
        else if (equal(coords[1], start)) geom.unshift(coords[0]);
        else if (equal(coords[1], end)) geom.push(coords[0]);
        else return; // If the overlap leaves the segment unchanged, return undefined so that this can be identified.

        // Otherwise return the mutated line.
        return line;
    }
}
